export function isOvernightWindow(start, end) {
    if (!start || !end || start === end) {
        return false;
    }
    return end < start;
}

export function isAllDayWindow(window) {
    return !!window?.allDay;
}

export function nextWeekDay(day) {
    return (Number(day) + 1) % 7;
}

export function windowTimesValid(start, end, allDay = false) {
    if (allDay) {
        return true;
    }
    if (!isValidTimeText(start) || !isValidTimeText(end)) {
        return false;
    }
    return start !== end;
}

export function isValidTimeText(value) {
    if (value == null || value === '') {
        return false;
    }
    const match = String(value).match(/^(\d{2}):(\d{2})$/);
    if (!match) {
        return false;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

export function normalizeTimeInput(value) {
    if (value == null) {
        return '';
    }
    const raw = String(value).trim();
    if (raw === '') {
        return '';
    }

    const digits = raw.replace(/\D/g, '');
    if (digits.length === 3 || digits.length === 4) {
        const padded = digits.padStart(4, '0');
        const hours = Number(padded.slice(0, 2));
        const minutes = Number(padded.slice(2));
        if (hours <= 23 && minutes <= 59) {
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }
    }

    const match = raw.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
    if (!match) {
        return raw;
    }
    const hours = Number(match[1]);
    const minutes = match[2] === undefined ? 0 : Number(match[2].padEnd(2, '0'));
    if (hours > 23 || minutes > 59) {
        return raw;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function reconnectWindowsInvalid(scheduleKind, dailyWindows, weeklyWindows) {
    const kind = Number(scheduleKind);
    if (kind === 0) {
        return false;
    }
    const windows = kind === 1 ? dailyWindows : weeklyWindows;
    return !Array.isArray(windows) || windows.length === 0
        || windows.some((window) => !windowTimesValid(
            window.start,
            window.end,
            kind === 2 && isAllDayWindow(window),
        ));
}

export function parseTimeValue(value) {
    if (value == null || value === '') {
        return '00:00';
    }
    const match = String(value).match(/^(\d{1,2}):(\d{2})/);
    if (!match) {
        return '00:00';
    }
    return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
}

export function mapWindowTimes(window, extra = {}) {
    const start = parseTimeValue(window?.Start ?? window?.StartText);
    const end = parseTimeValue(window?.End ?? window?.EndText);
    const { allowAllDay = false, ...rest } = extra;
    return {
        ...rest,
        start,
        end,
        allDay: allowAllDay && start === end,
    };
}

export function defaultDailyWindows() {
    return [{ start: '08:00', end: '18:00' }];
}

export function defaultWeeklyWindows() {
    return [{ day: 1, start: '08:00', end: '18:00', allDay: false }];
}

export function initReconnectState(reconnect) {
    const config = reconnect ?? {};
    const schedule = config.Schedule ?? {};
    const hasReconnect = reconnect != null;
    const maxRetry = config.MaxRetry;
    const unlimited = hasReconnect && maxRetry == null;
    const daily =
        Array.isArray(schedule.Daily) && schedule.Daily.length
            ? schedule.Daily.map((window) => mapWindowTimes(window))
            : defaultDailyWindows();
    const weekly =
        Array.isArray(schedule.Weekly) && schedule.Weekly.length
            ? schedule.Weekly.map((window) => mapWindowTimes(window, { day: Number(window.Day ?? 1), allowAllDay: true }))
            : defaultWeeklyWindows();
    const maxDelay = Number(config.MaxDelayRetry ?? config.MaxDelayRetrySeconds ?? 300);
    const retryDelay = Number(config.RetryDelay ?? config.RetryDelaySeconds ?? 5);

    return {
        unlimitedRetries: unlimited,
        useExponentialBackoff: unlimited && config.UseExponentialBackoff !== false,
        maxRetry: unlimited || maxRetry == null ? 10 : Math.min(500, Math.max(0, Number(maxRetry))),
        retryDelay: Math.min(3600, Math.max(1, Number.isFinite(retryDelay) ? retryDelay : 5)),
        maxDelayRetry: Math.min(3600, Math.max(30, Number.isFinite(maxDelay) ? maxDelay : 30)),
        scheduleKind: Number(schedule.Kind ?? 0),
        dailyWindows: daily,
        weeklyWindows: weekly,
    };
}

export function buildReconnectPayload({
    unlimitedRetries,
    useExponentialBackoff,
    maxRetry,
    retryDelay,
    maxDelayRetry,
    scheduleVisible,
    scheduleKind,
    dailyWindows,
    weeklyWindows,
}) {
    return {
        MaxRetry: unlimitedRetries ? null : Number(maxRetry),
        UseExponentialBackoff: unlimitedRetries && useExponentialBackoff,
        RetryDelay: Number(retryDelay),
        MaxDelayRetry: Number(maxDelayRetry),
        Schedule: {
            Kind: scheduleVisible ? Number(scheduleKind) : 0,
            Daily: scheduleVisible && Number(scheduleKind) === 1
                ? dailyWindows.map((window) => ({
                    Start: window.start,
                    End: window.end,
                }))
                : [],
            Weekly: scheduleVisible && Number(scheduleKind) === 2
                ? weeklyWindows.map((window) => ({
                    Day: Number(window.day),
                    Start: window.allDay ? '00:00' : window.start,
                    End: window.allDay ? '00:00' : window.end,
                }))
                : [],
        },
    };
}
